/**
 * Win-loss intelligence — turning closed deals into a learning loop.
 *
 * WHY this exists: every prospect that reaches closed-won or closed-lost
 * carries the single most valuable signal a pipeline produces — *why*.
 * Captured once at close (a reason + optional note), those answers roll
 * up here into win-rate, reasons-we-win, reasons-we-lose, and which
 * sources/personas actually convert. The same reasons feed back into
 * proposal findings so the next pitch leads with what closed the last
 * one and pre-empts what killed it.
 *
 * Honest accounting: this is deterministic and computed straight from
 * live `Person` records — no model, no inference. A deal with no reason
 * captured is counted in the win/loss totals but sits under
 * "(no reason given)" in the breakdowns, so the gaps are visible rather
 * than papered over.
 */

import type { Person } from '../db/types';

/** Curated reasons a deal is won — keeps the rollup clean (see SOURCE_OPTIONS
 *  rationale in PeopleView). "Other" is the free-text escape hatch. */
export const WON_REASONS = [
  'Clear ROI',
  'Product fit',
  'Trusted relationship',
  'Beat competitor',
  'Right timing',
  'Strong champion',
  'Price',
  'Referral / reputation',
  'Other',
] as const;

/** Curated reasons a deal is lost. */
export const LOST_REASONS = [
  'Price too high',
  'Lost to competitor',
  'No budget',
  'No decision / stalled',
  'Bad timing',
  'Missing feature',
  'No champion',
  'Went silent',
  'Other',
] as const;

export type WonReason = (typeof WON_REASONS)[number];
export type LostReason = (typeof LOST_REASONS)[number];

/** The reason taxonomy for a given closing stage. */
export function reasonsForStage(
  stage: 'closed-won' | 'closed-lost',
): readonly string[] {
  return stage === 'closed-won' ? WON_REASONS : LOST_REASONS;
}

const NO_REASON = '(no reason given)';

/** A counted reason line, sorted strongest-first for display. */
export interface ReasonTally {
  reason: string;
  count: number;
}

/** A source/persona conversion line. */
export interface SegmentStat {
  /** The segment label (source channel or persona archetype). */
  label: string;
  won: number;
  lost: number;
  /** 0–100 win rate within the segment; null when no closed deals. */
  winRate: number | null;
}

export interface WinLossSummary {
  /** Count of closed-won prospects. */
  won: number;
  /** Count of closed-lost prospects. */
  lost: number;
  /** 0–100 overall win rate; null when nothing has closed yet. */
  winRate: number | null;
  /** Total USD of closed-won estimatedValue. */
  wonValue: number;
  /** Total USD of closed-lost estimatedValue (pipeline that slipped). */
  lostValue: number;
  /** Why we win, strongest first. */
  wonReasons: ReasonTally[];
  /** Why we lose, strongest first. */
  lostReasons: ReasonTally[];
  /** Win rate by lead source, most-closed first. */
  bySource: SegmentStat[];
  /** Win rate by persona archetype, most-closed first. */
  byPersona: SegmentStat[];
}

const isWon = (p: Person): boolean =>
  p.category === 'prospect' && p.stage === 'closed-won';
const isLost = (p: Person): boolean =>
  p.category === 'prospect' && p.stage === 'closed-lost';

function tally(people: Person[]): ReasonTally[] {
  const counts = new Map<string, number>();
  for (const p of people) {
    const reason = p.winLossReason?.trim() || NO_REASON;
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

const rate = (won: number, lost: number): number | null =>
  won + lost === 0 ? null : Math.round((won / (won + lost)) * 100);

/** Group closed deals into win-rate-per-segment, most-closed segments first. */
function segment(
  closed: Person[],
  keyOf: (p: Person) => string | undefined,
): SegmentStat[] {
  const acc = new Map<string, { won: number; lost: number }>();
  for (const p of closed) {
    const label = keyOf(p)?.trim();
    if (!label) continue;
    const cur = acc.get(label) ?? { won: 0, lost: 0 };
    if (p.stage === 'closed-won') cur.won++;
    else cur.lost++;
    acc.set(label, cur);
  }
  return [...acc.entries()]
    .map(([label, { won, lost }]) => ({
      label,
      won,
      lost,
      winRate: rate(won, lost),
    }))
    .sort((a, b) => b.won + b.lost - (a.won + a.lost) || b.won - a.won);
}

/** Compute the full win-loss rollup from the live people roster. */
export function computeWinLoss(people: Person[]): WinLossSummary {
  const won = people.filter(isWon);
  const lost = people.filter(isLost);
  const closed = [...won, ...lost];
  const sumValue = (rows: Person[]): number =>
    rows.reduce((n, p) => n + (p.estimatedValue ?? 0), 0);

  return {
    won: won.length,
    lost: lost.length,
    winRate: rate(won.length, lost.length),
    wonValue: sumValue(won),
    lostValue: sumValue(lost),
    wonReasons: tally(won),
    lostReasons: tally(lost),
    bySource: segment(closed, (p) => p.source),
    byPersona: segment(closed, (p) => p.archetype),
  };
}

/**
 * One- to two-line plain-English read on the win-loss picture, for the
 * agent brief / proposal findings. Empty string when nothing has closed
 * (so callers can omit the line rather than print a hollow stat).
 */
export function winLossHeadline(summary: WinLossSummary): string {
  if (summary.winRate === null) return '';
  const bits = [
    `Win rate ${summary.winRate}% (${summary.won} won / ${summary.lost} lost).`,
  ];
  const topWin = summary.wonReasons.find((r) => r.reason !== NO_REASON);
  const topLoss = summary.lostReasons.find((r) => r.reason !== NO_REASON);
  if (topWin) bits.push(`Most cited win driver: ${topWin.reason.toLowerCase()}.`);
  if (topLoss) bits.push(`Most cited loss driver: ${topLoss.reason.toLowerCase()}.`);
  return bits.join(' ');
}
